<template>
  <div
    class="bg-[#020136] sticky z-50 top-12 font-sans text-white text-2xl py-3 font-bold"
  >
    <div class="container">
      <span class="md:px-11">{{ $t('ads.nativeAds1') }}</span>
    </div>
  </div>
  <div class="pt-4 pb-16 container bg-gray-100">
    <template v-if="article">
      <div class="grid-container md:px-11">
        <ArticleTop
          class="col-span-6 sm:col-span-12 mb-5 md:mb-6 pt-4"
          :article="article"
        />
        <h1
          class="headline-m col-span-6 sm:col-span-12"
          :class="{
            '!font-[georgia]': sponsored,
            'mb-3 md:mb-5': isMm(),
            'mb-5 md:mb-8': !isMm(),
          }"
          v-html="article.headline"
        />
        <div
          v-if="article.mainTeaser"
          class="standfirst col-span-6 sm:col-span-10 mb-5 md:mb-8 content-dynamic"
          v-html="article.mainTeaser"
          :class="{
            '!font-[georgia]': sponsored,
          }"
        />
        <div class="col-span-6 -mx-4 md:mx-0 sm:col-span-10">
          <figure
            v-if="!article.toppictHTML && mainPicture"
            class="mb-3 md:mb-10 max-w-5xl"
          >
            <NuxtPicture
              format="webp"
              sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw"
              quality="80"
              class="w-full"
              :img-attrs="{ class: 'w-full' }"
              :src="mainPicture.url ?? undefined"
              :alt="mainPicture.text ?? undefined"
              loading="eager"
              :preload="true"
            />
            <figcaption class="max-w-5xl captions mt-2 md:mt-3 px-4 md:px-0">
              <span v-if="mainPicture.text" v-html="mainPicture.text" />
              <span v-if="mainPicture.photographer" class="opacity-35 pl-1"
                >Foto: {{ mainPicture.photographer }}</span
              >
            </figcaption>
          </figure>
        </div>
      </div>
      <div class="grid-container">
        <div class="col-span-6 sm:col-span-1">
          <ArticleToolbar
            class="hidden md:inline-flex"
            v-if="article.id"
            :article-id="article.id"
          />
        </div>
        <div class="col-span-6 sm:col-span-8 content">
          <div
            v-if="article.customWriter"
            class="font-[georgia] mb-8 flex items-center"
          >
            <NuxtImg
              :src="article.customWriterImageUrl"
              height="64"
              v-if="article.customWriterImageUrl"
              class="mr-4"
            />
            <strong class="mr-2">{{ article.customWriter }}</strong>
            <span
              v-if="article.customWriterDescription"
              class="text-gray-600"
              >{{ article.customWriterDescription }}</span
            >
          </div>
          <ArticleContent
            class="col-span-6 sm:col-span-8 content font-[georgia]"
            :article="article"
          />
        </div>
        <aside class="col-span-6 mt-8 md:mt-0 sm:col-span-3">
          <div class="sidebar no-print">
            <template v-if="!sponsored">
              <ListMostRead />
              <ListNames :count="5" />
            </template>
            <template v-else>
              <ListJobs />
            </template>
          </div>
        </aside>
      </div>
    </template>
  </div>
  <LayoutStaticPageBottom />
</template>
<script setup lang="ts">
const nuxtApp = useNuxtApp()
const route = useRoute()
const config = useRuntimeConfig()
const userStore = useUserStore()
const indexStore = useIndexStore()
const currentPaperId = indexStore.currentPaper?.RecordId ?? 1

const cacheKey = `sponsored-${route.params.id.toString()}-${
  userStore.isLoggedIn
}`
const { data: article, error } = await useAsyncData(cacheKey, async () => {
  return await nuxtApp.$api.content.articleFull(
    route.params.id.toString(),
    currentPaperId
  )
})
if (error.value) {
  throw createError({ statusCode: 404, message: 'Sponsored article not found' })
}

const mainPicture = computed(() => {
  return article.value?.images?.find((image) => image.toppict === 1)
})

const sponsored = computed(
  () => article.value?.type?.name == 'Sponsored content'
)
const titlePrefix = computed(() =>
  sponsored ? `${nuxtApp.$t('ads.nativeAds1')}: ` : ''
)
useServerSeoMeta({
  title: `${titlePrefix.value}${article.value?.headline}${config.public.site.meta.title.postfix.long}`,
  description: article.value?.mainTeaser ?? undefined,
  ogImage: mainPicture.value?.url ?? undefined,
  ogImageAlt: mainPicture.value?.text ?? undefined,
})
</script>
<style lang="scss" scoped>
.iframe {
  :deep() {
    iframe {
      width: 100%;
      height: 660px;
    }
  }
}
.content {
  max-width: 720px;
  min-width: 300px;
  flex-basis: auto;
  flex-grow: 1;
}
.writer-description {
  :deep() {
    p {
      margin-bottom: 10px;
    }
  }
}
.content {
  :deep() {
    > .quote {
      @apply border border-gray-300 text-black font-[georgia] px-8 py-6 rounded-md;
    }
    > .quote > p {
      @apply text-black mb-8;
    }
    > .quote > p:before {
      @apply text-5xl;
    }
  }
}
.sidebar {
  :deep() {
    > div {
      @apply mb-10;
      @screen md {
        @apply mb-4;
        &:last-child {
          @apply mb-0;
        }
      }
      @screen lg {
        @apply mb-8;
        &:last-child {
          @apply mb-0;
        }
      }
      @screen xl {
        @apply mb-12;
        &:last-child {
          @apply mb-0;
        }
      }
    }
  }
}

.image {
  figcaption {
    caption-side: bottom;
  }
}
</style>
<style>
.enlargeFirstletter > p:first-child::first-letter {
  font-family: EamesCenturyModern;
  font-size: 60px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  color: #282828;
  line-height: 45px;
  float: left;
  padding-right: 3px;
}

table {
  background-color: transparent;
}

caption {
  padding-top: 8px;
  padding-bottom: 8px;
  color: #9aa2a5;
  text-align: left;
}

th {
  text-align: left;
}

.table {
  width: 100%;
  max-width: 100%;
  margin-bottom: 20px;
}
.table > thead > tr > th,
.table > thead > tr > td,
.table > tbody > tr > th,
.table > tbody > tr > td,
.table > tfoot > tr > th,
.table > tfoot > tr > td {
  padding: 8px;
  line-height: 1.428571429;
  vertical-align: top;
  border-top: 1px solid #ddd;
}
.table > thead > tr > th {
  vertical-align: bottom;
  border-bottom: 2px solid #ddd;
}
.table > caption + thead > tr:first-child > th,
.table > caption + thead > tr:first-child > td,
.table > colgroup + thead > tr:first-child > th,
.table > colgroup + thead > tr:first-child > td,
.table > thead:first-child > tr:first-child > th,
.table > thead:first-child > tr:first-child > td {
  border-top: 0;
}
.table > tbody + tbody {
  border-top: 2px solid #ddd;
}
.table .table {
  background-color: #fcfdfd;
}

.table-condensed > thead > tr > th,
.widget-documentation .table > thead > tr > th,
.table-condensed > thead > tr > td,
.widget-documentation .table > thead > tr > td,
.table-condensed > tbody > tr > th,
.widget-documentation .table > tbody > tr > th,
.table-condensed > tbody > tr > td,
.widget-documentation .table > tbody > tr > td,
.table-condensed > tfoot > tr > th,
.widget-documentation .table > tfoot > tr > th,
.table-condensed > tfoot > tr > td,
.widget-documentation .table > tfoot > tr > td {
  padding: 5px;
}

.table-bordered {
  border: 1px solid #ddd;
}
.table-bordered > thead > tr > th,
.table-bordered > thead > tr > td,
.table-bordered > tbody > tr > th,
.table-bordered > tbody > tr > td,
.table-bordered > tfoot > tr > th,
.table-bordered > tfoot > tr > td {
  border: 1px solid #ddd;
}
.table-bordered > thead > tr > th,
.table-bordered > thead > tr > td {
  border-bottom-width: 2px;
}

.table-striped > tbody > tr:nth-of-type(odd) {
  background-color: #f9f9f9;
}

.table-hover > tbody > tr:hover {
  background-color: #f5f5f5;
}

table col[class*='col-'] {
  position: static;
  float: none;
  display: table-column;
}

table td[class*='col-'],
table th[class*='col-'] {
  position: static;
  float: none;
  display: table-cell;
}

.table > thead > tr > td.active,
.table > thead > tr > th.active,
.table > thead > tr.active > td,
.table > thead > tr.active > th,
.table > tbody > tr > td.active,
.table > tbody > tr > th.active,
.table > tbody > tr.active > td,
.table > tbody > tr.active > th,
.table > tfoot > tr > td.active,
.table > tfoot > tr > th.active,
.table > tfoot > tr.active > td,
.table > tfoot > tr.active > th {
  background-color: #f5f5f5;
}

.table-hover > tbody > tr > td.active:hover,
.table-hover > tbody > tr > th.active:hover,
.table-hover > tbody > tr.active:hover > td,
.table-hover > tbody > tr:hover > .active,
.table-hover > tbody > tr.active:hover > th {
  background-color: #e8e8e8;
}

.table > thead > tr > td.success,
.table > thead > tr > th.success,
.table > thead > tr.success > td,
.table > thead > tr.success > th,
.table > tbody > tr > td.success,
.table > tbody > tr > th.success,
.table > tbody > tr.success > td,
.table > tbody > tr.success > th,
.table > tfoot > tr > td.success,
.table > tfoot > tr > th.success,
.table > tfoot > tr.success > td,
.table > tfoot > tr.success > th {
  background-color: #dff0d8;
}

.table-hover > tbody > tr > td.success:hover,
.table-hover > tbody > tr > th.success:hover,
.table-hover > tbody > tr.success:hover > td,
.table-hover > tbody > tr:hover > .success,
.table-hover > tbody > tr.success:hover > th {
  background-color: #d0e9c6;
}

.table > thead > tr > td.info,
.table > thead > tr > th.info,
.table > thead > tr.info > td,
.table > thead > tr.info > th,
.table > tbody > tr > td.info,
.table > tbody > tr > th.info,
.table > tbody > tr.info > td,
.table > tbody > tr.info > th,
.table > tfoot > tr > td.info,
.table > tfoot > tr > th.info,
.table > tfoot > tr.info > td,
.table > tfoot > tr.info > th {
  background-color: #d9edf7;
}

.table-hover > tbody > tr > td.info:hover,
.table-hover > tbody > tr > th.info:hover,
.table-hover > tbody > tr.info:hover > td,
.table-hover > tbody > tr:hover > .info,
.table-hover > tbody > tr.info:hover > th {
  background-color: #c4e3f3;
}

.table > thead > tr > td.warning,
.table > thead > tr > th.warning,
.table > thead > tr.warning > td,
.table > thead > tr.warning > th,
.table > tbody > tr > td.warning,
.table > tbody > tr > th.warning,
.table > tbody > tr.warning > td,
.table > tbody > tr.warning > th,
.table > tfoot > tr > td.warning,
.table > tfoot > tr > th.warning,
.table > tfoot > tr.warning > td,
.table > tfoot > tr.warning > th {
  background-color: #fcf8e3;
}

.table-hover > tbody > tr > td.warning:hover,
.table-hover > tbody > tr > th.warning:hover,
.table-hover > tbody > tr.warning:hover > td,
.table-hover > tbody > tr:hover > .warning,
.table-hover > tbody > tr.warning:hover > th {
  background-color: #faf2cc;
}

.table > thead > tr > td.danger,
.table > thead > tr > th.danger,
.table > thead > tr.danger > td,
.table > thead > tr.danger > th,
.table > tbody > tr > td.danger,
.table > tbody > tr > th.danger,
.table > tbody > tr.danger > td,
.table > tbody > tr.danger > th,
.table > tfoot > tr > td.danger,
.table > tfoot > tr > th.danger,
.table > tfoot > tr.danger > td,
.table > tfoot > tr.danger > th {
  background-color: #f2dede;
}

.table-hover > tbody > tr > td.danger:hover,
.table-hover > tbody > tr > th.danger:hover,
.table-hover > tbody > tr.danger:hover > td,
.table-hover > tbody > tr:hover > .danger,
.table-hover > tbody > tr.danger:hover > th {
  background-color: #ebcccc;
}

.table-responsive {
  overflow-x: auto;
  min-height: 0.01%;
}
@media screen and (max-width: 767px) {
  .table-responsive {
    width: 100%;
    margin-bottom: 15px;
    overflow-y: hidden;
    -ms-overflow-style: -ms-autohiding-scrollbar;
    border: 1px solid #ddd;
  }
  .table-responsive > .table {
    margin-bottom: 0;
  }
  .table-responsive > .table > thead > tr > th,
  .table-responsive > .table > thead > tr > td,
  .table-responsive > .table > tbody > tr > th,
  .table-responsive > .table > tbody > tr > td,
  .table-responsive > .table > tfoot > tr > th,
  .table-responsive > .table > tfoot > tr > td {
    white-space: nowrap;
  }
  .table-responsive > .table-bordered {
    border: 0;
  }
  .table-responsive > .table-bordered > thead > tr > th:first-child,
  .table-responsive > .table-bordered > thead > tr > td:first-child,
  .table-responsive > .table-bordered > tbody > tr > th:first-child,
  .table-responsive > .table-bordered > tbody > tr > td:first-child,
  .table-responsive > .table-bordered > tfoot > tr > th:first-child,
  .table-responsive > .table-bordered > tfoot > tr > td:first-child {
    border-left: 0;
  }
  .table-responsive > .table-bordered > thead > tr > th:last-child,
  .table-responsive > .table-bordered > thead > tr > td:last-child,
  .table-responsive > .table-bordered > tbody > tr > th:last-child,
  .table-responsive > .table-bordered > tbody > tr > td:last-child,
  .table-responsive > .table-bordered > tfoot > tr > th:last-child,
  .table-responsive > .table-bordered > tfoot > tr > td:last-child {
    border-right: 0;
  }
  .table-responsive > .table-bordered > tbody > tr:last-child > th,
  .table-responsive > .table-bordered > tbody > tr:last-child > td,
  .table-responsive > .table-bordered > tfoot > tr:last-child > th,
  .table-responsive > .table-bordered > tfoot > tr:last-child > td {
    border-bottom: 0;
  }
}
</style>
